<script lang="ts" setup>
import { useDialog } from '@/composeables';
import { ref } from 'vue';
import DialogSystemModalInstance from './DialogSystemModalInstance.vue';
import type { IDialogSystemModalInstanceExposed } from './IDialogSystemModalInstanceExposed';
import type { IDialogInstance } from '@/composeables/dialog/IDialogInstance';

const dialogStack = useDialog().GetDialogStack();
const dialogInstanceStack = ref<IDialogSystemModalInstanceExposed[]>([]);

/**
 * Handle close on escape
 *
 * TODO: This can actually get the correct dialogInstance immediatly so we dont have to do a lookup on the key, but then we are not guaranteed to be on the top of the stack
 * Leave it as is for now
 */
const escapeKeyDown = async (_event: KeyboardEvent) => {
    // Find the topmost dialog
    const topDialog = dialogStack.value.at(-1);

    // If the topmost dialog supports hideOnEscape then we close it
    if (topDialog?.EnableHideOnEscape) {
        // Here we find the vue component instance and request it close
        await dialogInstanceStack.value.find((d) => d.UniqueLoopKey === topDialog.UniqueLoopKey)?.requestClose();
    }
};

/**
 * The dialog instance will trigger this when bootstrap has finished closing the dialog, so we can remove it from the dom
 */
const dialogFullyClosed = (uniqueLoopKey: string) => {
    const topDialog = dialogStack.value.at(-1);
    if (topDialog?.UniqueLoopKey !== uniqueLoopKey) {
        // All good, the top dialog is being closed
        console.warn(
            `Dialog that is being closed is not the topmost dialog. Closed dialog at index ${dialogStack.value.findIndex((d) => d.UniqueLoopKey == uniqueLoopKey)}. Dialogstack length is ${dialogStack.value.length}. Expected: ${topDialog?.UniqueLoopKey} with title ${topDialog?.Title ?? 'not_found'}, got: ${uniqueLoopKey} with title ${dialogStack.value.find((d) => d.UniqueLoopKey == uniqueLoopKey)?.Title ?? 'not_found'}`,
        );
    }
    // Remove the one we want to remove
    dialogStack.value = dialogStack.value.filter((d) => d.UniqueLoopKey !== uniqueLoopKey);
};

/**
 * Set the modelvalue when its updated.
 *
 * We do this here instead of inside the dialog instance since here the vmodel isnt a prop :)
 */
const setDialogInstanceModelValue = <TComponent extends new (...args: any) => any, TModelValue>(
    newValue: TModelValue,
    dialog: IDialogInstance<TComponent, any, TModelValue>,
) => {
    if (!dialog.VModel) {
        return;
    }
    dialog.VModel.value = newValue;
};

/**
 * Updates a named modelvalue
 */
const setDialogInstanceNamedModelValue = <TComponent extends new (...args: any) => any>(
    key: string,
    value: any,
    dialog: IDialogInstance<TComponent>,
) => {
    if (key in (dialog.NamedVModels ?? {})) {
        dialog.NamedVModels![key].value = value;
    }
};
</script>
<template>
    <DialogSystemModalInstance
        v-for="dialog in dialogStack"
        :key="dialog.UniqueLoopKey"
        v-bind="dialog"
        @escapeKeyDown="escapeKeyDown"
        ref="dialogInstanceStack"
        @dialog-fully-closed="dialogFullyClosed"
        :model-value="dialog.VModel?.value"
        @update:model-value="(val) => setDialogInstanceModelValue(val, dialog)"
        @updateNamedModelValue="(key, val) => setDialogInstanceNamedModelValue(key, val, dialog)"
    />
</template>
